import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../../context/auth-context.jsx';
import { adminApi } from '../../../lib/admin-api/index.js';
import {
  ADMIN_NOTIFICATION_TYPES,
  useAdminNotificationRefresh,
} from '../../../shared/realtime/admin-notification-events.js';
import { buildTransactionsRequestDto, mapTransactionsResponseToDto } from '../dto/transactions-list.dto.js';
import { buildTransactionsTableModel } from '../model/transactions-list.model.js';

const TRANSACTION_TABS = [
  { key: 'deposit', label: 'Deposits' },
  { key: 'withdrawal', label: 'Withdrawals' },
  { key: 'own_transfer', label: 'Internal Transfers' },
];

const EMPTY_ROWS_BY_TAB = {
  deposit: [],
  withdrawal: [],
  own_transfer: [],
};

const TRANSACTION_PAGE_SIZE = 20;
const TRANSACTION_TAB_KEYS = TRANSACTION_TABS.map((tab) => tab.key);
const TRANSACTION_FILTER_STORAGE_KEY = 'admin_transactions_list_filters';
const DEFAULT_TRANSACTION_TYPE = 'deposit';
const TRANSACTION_NOTIFICATION_TYPES = Object.freeze([
  ADMIN_NOTIFICATION_TYPES.depositInitiated,
  ADMIN_NOTIFICATION_TYPES.withdrawalInitiated,
]);
const TRANSACTION_STATUS_OPTIONS = ['created', 'pending', 'processed', 'failed', 'succeed', 'canceled', 'resolution', 'pending_input'];
const EMPTY_TRANSACTION_FILTERS = Object.freeze({
  type: DEFAULT_TRANSACTION_TYPE,
  status: '',
  method: '',
  account: '',
  transactionNumber: '',
  reference: '',
  profileName: '',
  profileEmail: '',
  countriesOnly: [],
});

function readSearchParams() {
  if (typeof window === 'undefined') {
    return new URLSearchParams();
  }

  return new URLSearchParams(window.location.search || '');
}

function readStringParam(params, key) {
  return String(params.get(key) || '').trim();
}

function normalizeTransactionFilters(filters) {
  const requestedType = String(filters?.type || '').trim().toLowerCase();

  return {
    ...EMPTY_TRANSACTION_FILTERS,
    ...filters,
    type: TRANSACTION_TAB_KEYS.includes(requestedType) ? requestedType : DEFAULT_TRANSACTION_TYPE,
    status: String(filters?.status || '').trim().toLowerCase(),
    method: String(filters?.method || '').trim(),
    account: String(filters?.account || '').trim(),
    transactionNumber: String(filters?.transactionNumber || '').trim(),
    reference: String(filters?.reference || '').trim(),
    profileName: String(filters?.profileName || '').trim(),
    profileEmail: String(filters?.profileEmail || '').trim(),
    countriesOnly: Array.isArray(filters?.countriesOnly)
      ? filters.countriesOnly.map((item) => String(item || '').trim().toUpperCase()).filter(Boolean)
      : [],
  };
}

function getInitialFiltersFromUrl() {
  const params = readSearchParams();

  return normalizeTransactionFilters({
    type: readStringParam(params, 'type'),
    status: readStringParam(params, 'status'),
    method: readStringParam(params, 'method'),
    account: readStringParam(params, 'account'),
    transactionNumber: readStringParam(params, 'transactionNumber'),
    reference: readStringParam(params, 'reference'),
    profileName: readStringParam(params, 'profileName'),
    profileEmail: readStringParam(params, 'profileEmail'),
    countriesOnly: params.getAll('country').map((item) => String(item || '').trim().toUpperCase()).filter(Boolean),
  });
}

function readStoredFilters() {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const raw = window.sessionStorage.getItem(TRANSACTION_FILTER_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

function hasActiveFilters(filters) {
  const normalized = normalizeTransactionFilters(filters);

  return Boolean(
    normalized.type !== DEFAULT_TRANSACTION_TYPE
      || normalized.status
      || normalized.method
      || normalized.account
      || normalized.transactionNumber
      || normalized.reference
      || normalized.profileName
      || normalized.profileEmail
      || normalized.countriesOnly.length > 0,
  );
}

function getInitialTransactionFilters() {
  const urlFilters = getInitialFiltersFromUrl();
  if (hasActiveFilters(urlFilters)) {
    return urlFilters;
  }

  return normalizeTransactionFilters(readStoredFilters() || EMPTY_TRANSACTION_FILTERS);
}

function buildFilterSearchParams(filters) {
  const normalized = normalizeTransactionFilters(filters);
  const params = new URLSearchParams();

  if (normalized.type !== DEFAULT_TRANSACTION_TYPE) {
    params.set('type', normalized.type);
  }
  if (normalized.status) {
    params.set('status', normalized.status);
  }
  if (normalized.method) {
    params.set('method', normalized.method);
  }
  if (normalized.account) {
    params.set('account', normalized.account);
  }
  if (normalized.transactionNumber) {
    params.set('transactionNumber', normalized.transactionNumber);
  }
  if (normalized.reference) {
    params.set('reference', normalized.reference);
  }
  if (normalized.profileName) {
    params.set('profileName', normalized.profileName);
  }
  if (normalized.profileEmail) {
    params.set('profileEmail', normalized.profileEmail);
  }
  normalized.countriesOnly.forEach((country) => {
    params.append('country', country);
  });

  return params;
}

function persistTransactionFilters(filters) {
  if (typeof window === 'undefined') {
    return;
  }

  const normalized = normalizeTransactionFilters(filters);
  const active = hasActiveFilters(normalized);

  try {
    if (active) {
      window.sessionStorage.setItem(TRANSACTION_FILTER_STORAGE_KEY, JSON.stringify(normalized));
    } else {
      window.sessionStorage.removeItem(TRANSACTION_FILTER_STORAGE_KEY);
    }
  } catch {
    // ignore unavailable storage
  }

  const params = buildFilterSearchParams(normalized);
  const nextSearch = params.toString();
  const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}${window.location.hash || ''}`;
  window.history.replaceState(window.history.state, '', nextUrl);
}

function mapCountryOptions(payload) {
  const raw = payload?.countries ?? payload?.items ?? payload?.data ?? payload;
  const list = Array.isArray(raw) ? raw : (raw && typeof raw === 'object' ? Object.values(raw) : []);

  const options = list
    .map((item) => {
      if (typeof item === 'string') {
        const value = item.trim().toUpperCase();
        return value ? { value, label: value } : null;
      }

      if (!item || typeof item !== 'object') {
        return null;
      }

      const value = String(item.code || item.value || item.country || '').trim().toUpperCase();
      if (!value) {
        return null;
      }

      const name = String(item.name || item.label || '').trim();
      return { value, label: name ? `${value} - ${name}` : value };
    })
    .filter(Boolean);

  const uniq = new Map();
  options.forEach((option) => uniq.set(option.value, option));
  return Array.from(uniq.values()).sort((a, b) => a.value.localeCompare(b.value));
}

export function useTransactionsList() {
  const { token } = useAuth();
  const initialFilters = useMemo(() => getInitialTransactionFilters(), []);
  const [activeTab, setActiveTab] = useState(initialFilters.type);
  const [filters, setFilters] = useState(initialFilters);
  const [draftFilters, setDraftFilters] = useState(initialFilters);
  const [isFilterOpen, setIsFilterOpen] = useState(() => hasActiveFilters(initialFilters));
  const [countryOptions, setCountryOptions] = useState([]);
  const [rowsByTab, setRowsByTab] = useState(EMPTY_ROWS_BY_TAB);
  const [loadingByTab, setLoadingByTab] = useState(() => ({
    deposit: initialFilters.type === 'deposit',
    withdrawal: initialFilters.type === 'withdrawal',
    own_transfer: initialFilters.type === 'own_transfer',
  }));
  const [loadingMoreByTab, setLoadingMoreByTab] = useState({
    deposit: false,
    withdrawal: false,
    own_transfer: false,
  });
  const [hasMoreByTab, setHasMoreByTab] = useState({
    deposit: false,
    withdrawal: false,
    own_transfer: false,
  });
  const [loadedTabs, setLoadedTabs] = useState({ deposit: false, withdrawal: false, own_transfer: false });
  const [error, setError] = useState('');

  useEffect(() => {
    persistTransactionFilters({ ...filters, type: activeTab });
  }, [activeTab, filters]);

  useEffect(() => {
    let isCancelled = false;

    const loadCountries = async () => {
      try {
        const response = await adminApi.getCountryDictionary(token);
        if (!isCancelled) {
          setCountryOptions(mapCountryOptions(response));
        }
      } catch {
        if (!isCancelled) {
          setCountryOptions([]);
        }
      }
    };

    loadCountries();

    return () => {
      isCancelled = true;
    };
  }, [token]);

  const loadTransactions = useCallback(async (type, { force = false, offset = 0, append = false } = {}) => {
    const normalizedType = String(type || '').trim().toLowerCase();
    if (!normalizedType) {
      return;
    }

    if (!force && !append && loadedTabs[normalizedType]) {
      return;
    }

    if (append) {
      setLoadingMoreByTab((prev) => ({
        ...prev,
        [normalizedType]: true,
      }));
    } else {
      setLoadingByTab((prev) => ({
        ...prev,
        [normalizedType]: true,
      }));
    }

    if (normalizedType === activeTab) {
      setError('');
    }

    try {
      const response = await adminApi.getTransactions(token, buildTransactionsRequestDto(normalizedType, {
        limit: TRANSACTION_PAGE_SIZE,
        offset,
      }, filters));
      const dto = mapTransactionsResponseToDto(response);
      const rows = buildTransactionsTableModel(dto);

      setRowsByTab((prev) => ({
        ...prev,
        [normalizedType]: append ? [...(prev[normalizedType] || []), ...rows] : rows,
      }));
      setHasMoreByTab((prev) => ({
        ...prev,
        [normalizedType]: rows.length === TRANSACTION_PAGE_SIZE,
      }));
      setLoadedTabs((prev) => ({
        ...prev,
        [normalizedType]: true,
      }));
    } catch (requestError) {
      if (!append) {
        setRowsByTab((prev) => ({
          ...prev,
          [normalizedType]: [],
        }));
        setHasMoreByTab((prev) => ({
          ...prev,
          [normalizedType]: false,
        }));
      }

      if (normalizedType === activeTab) {
        setError(requestError.message || 'Failed to load transactions');
      }
    } finally {
      if (append) {
        setLoadingMoreByTab((prev) => ({
          ...prev,
          [normalizedType]: false,
        }));
      } else {
        setLoadingByTab((prev) => ({
          ...prev,
          [normalizedType]: false,
        }));
      }
    }
  }, [activeTab, filters, loadedTabs, token]);

  useEffect(() => {
    loadTransactions(activeTab);
  }, [activeTab, loadTransactions]);

  useAdminNotificationRefresh(TRANSACTION_NOTIFICATION_TYPES, (notification) => {
    const targetTab = notification?.type === ADMIN_NOTIFICATION_TYPES.withdrawalInitiated
      ? 'withdrawal'
      : 'deposit';

    if (targetTab === activeTab) {
      loadTransactions(targetTab, { force: true });
      return;
    }

    setLoadedTabs((prev) => ({
      ...prev,
      [targetTab]: false,
    }));
  });

  const selectTab = useCallback((tab) => {
    const normalizedTab = String(tab || '').trim().toLowerCase();
    if (!normalizedTab || normalizedTab === activeTab) {
      return;
    }

    setActiveTab(normalizedTab);
    setFilters((currentFilters) => ({ ...currentFilters, type: normalizedTab }));
    setDraftFilters((currentFilters) => ({ ...currentFilters, type: normalizedTab }));
    setError('');
  }, [activeTab]);

  const reloadActiveTab = useCallback(async () => {
    await loadTransactions(activeTab, { force: true });
  }, [activeTab, loadTransactions]);

  const rows = useMemo(() => rowsByTab[activeTab] || [], [activeTab, rowsByTab]);
  const isLoading = Boolean(loadingByTab[activeTab]);
  const isLoadingMore = Boolean(loadingMoreByTab[activeTab]);
  const hasMoreTransactions = Boolean(hasMoreByTab[activeTab]);

  const loadMoreTransactions = useCallback(() => {
    if (isLoading || isLoadingMore || !hasMoreTransactions) {
      return;
    }

    loadTransactions(activeTab, {
      offset: rows.length,
      append: true,
    });
  }, [activeTab, hasMoreTransactions, isLoading, isLoadingMore, loadTransactions, rows.length]);

  const updateFilter = useCallback((event) => {
    const { name, value } = event.target;
    setDraftFilters((currentFilters) => ({
      ...currentFilters,
      [name]: String(value || ''),
    }));
  }, []);

  const applyFilters = useCallback(() => {
    setFilters(normalizeTransactionFilters({ ...draftFilters, type: activeTab }));
    setLoadedTabs({ deposit: false, withdrawal: false, own_transfer: false });
    setError('');
  }, [activeTab, draftFilters]);

  const clearFilters = useCallback(() => {
    const emptyFilters = normalizeTransactionFilters({
      ...EMPTY_TRANSACTION_FILTERS,
      type: activeTab,
    });
    setDraftFilters(emptyFilters);
    setFilters(emptyFilters);
    setLoadedTabs({ deposit: false, withdrawal: false, own_transfer: false });
    setIsFilterOpen(false);
    setError('');
  }, [activeTab]);

  return {
    tabs: TRANSACTION_TABS,
    activeTab,
    rows,
    isLoading,
    isLoadingMore,
    hasMoreTransactions,
    filters: draftFilters,
    statusOptions: TRANSACTION_STATUS_OPTIONS,
    countryOptions,
    isFilterOpen,
    error,
    selectTab,
    reloadActiveTab,
    loadMoreTransactions,
    updateFilter,
    onCountriesFilterChange: (countries) => setDraftFilters((prev) => ({
      ...prev,
      countriesOnly: Array.isArray(countries)
        ? countries.map((item) => String(item || '').trim().toUpperCase()).filter(Boolean)
        : [],
    })),
    applyFilters,
    clearFilters,
    setIsFilterOpen,
  };
}
